import type { CSSProperties } from "react";
import { z } from "zod";
import type { MediaAssetSlim } from "@/lib/media";
import { mediaAssetSlimSchema } from "@/validations/media";

/**
 * Brand theming for any surface that wants its own look — a partner's menu
 * ("Food & drinks by Loudmouth"), and later a storefront, an event, a hiive,
 * a venue or a chat. Each owner stores one `brand_theme` JSON column and the
 * rest is shared: the schema below, `resolveBrandTheme` and
 * `brandThemeStyle`.
 *
 * Four authored colours, everything else derived:
 * - `surface` — the branded panel's background.
 * - `ink` — text on it. Optional; derived for contrast when absent or weak.
 * - `accent` — the primary brand colour: buttons, the selected state.
 * - `highlight` — an optional second colour for small emphasis (deadlines).
 *
 * A branded block is mode-independent: it looks the same in light and dark,
 * like a printed card, so it never needs a second palette.
 *
 * No flash, by construction: resolution is pure and deterministic, runs on the
 * server, and its output is inline CSS variables in the server-rendered HTML.
 * Nothing is fetched or computed after hydration.
 */

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export const hexColorSchema = z
  .string()
  .trim()
  .regex(HEX, "Use a hex colour like #B7FF00.")
  .transform(normalizeHex);

export const brandThemeSchema = z
  .object({
    v: z.literal(1),
    surface: hexColorSchema,
    ink: hexColorSchema.nullable().default(null),
    accent: hexColorSchema,
    highlight: hexColorSchema.nullable().default(null),
    /** Optional logo; without one the partner's name is set as a wordmark. */
    logo: mediaAssetSlimSchema.nullable().default(null),
  })
  .strict();

export type BrandTheme = {
  v: 1;
  surface: string;
  ink: string | null;
  accent: string;
  highlight: string | null;
  logo: MediaAssetSlim | null;
};

/** A logo as the client renders it (same shape as an add-on image). */
export type BrandImage = {
  url: string;
  blurHash: string | null;
  aspectRatio: number;
};

/** Every colour a branded block uses, already contrast-checked. */
export type ResolvedBrandTheme = {
  surface: string;
  /** A step lighter/darker than the surface, for rows and inputs. */
  raised: string;
  border: string;
  ink: string;
  inkMuted: string;
  accent: string;
  accentInk: string;
  /** The accent where it can be read as text on the surface, else the ink. */
  accentText: string;
  highlight: string;
  highlightInk: string;
  logo: BrandImage | null;
};

/** WCAG AA for body text. */
export const MIN_TEXT_CONTRAST = 4.5;
/** WCAG AA for large/bold text and UI glyphs. */
const MIN_LARGE_TEXT_CONTRAST = 3;

export function normalizeHex(value: string) {
  const hex = value.trim().replace(/^#/, "").toUpperCase();
  return `#${hex.length === 3 ? [...hex].map((c) => c + c).join("") : hex}`;
}

function channels(hex: string): [number, number, number] {
  const value = Number.parseInt(normalizeHex(hex).slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function luminance(hex: string) {
  const [r, g, b] = channels(hex).map((channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: string, b: string) {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** Black or white, whichever reads better on `background`. */
export function readableInk(background: string) {
  return contrastRatio("#000000", background) >=
    contrastRatio("#FFFFFF", background)
    ? "#000000"
    : "#FFFFFF";
}

/** `amount` (0–1) of `top` over `base`, as hex — no `color-mix()` needed. */
function mix(base: string, top: string, amount: number) {
  const a = channels(base);
  const b = channels(top);
  return `#${a
    .map((channel, index) =>
      Math.round(channel + (b[index] - channel) * amount)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()}`;
}

function inkOn(background: string, preferred: string | null) {
  return preferred && contrastRatio(preferred, background) >= MIN_TEXT_CONTRAST
    ? preferred
    : readableInk(background);
}

/**
 * Authored theme → every colour a block needs. Never trusts the author's ink
 * blindly: a pairing that fails WCAG AA falls back to black or white.
 */
export function resolveBrandTheme(
  theme: Pick<BrandTheme, "surface" | "ink" | "accent" | "highlight">,
  logo: BrandImage | null = null,
): ResolvedBrandTheme {
  const surface = normalizeHex(theme.surface);
  const accent = normalizeHex(theme.accent);
  const highlight = normalizeHex(theme.highlight ?? theme.accent);
  const ink = inkOn(surface, theme.ink ? normalizeHex(theme.ink) : null);
  return {
    surface,
    raised: mix(surface, ink, 0.07),
    border: mix(surface, ink, 0.16),
    ink,
    inkMuted: mix(surface, ink, 0.7),
    accent,
    accentInk: readableInk(accent),
    accentText:
      contrastRatio(accent, surface) >= MIN_LARGE_TEXT_CONTRAST ? accent : ink,
    highlight,
    highlightInk: readableInk(highlight),
    logo,
  };
}

/** Problems worth telling the author about before they save. */
export function brandThemeWarnings(
  theme: Pick<BrandTheme, "surface" | "ink" | "accent">,
): string[] {
  const warnings: string[] = [];
  if (
    theme.ink &&
    contrastRatio(normalizeHex(theme.ink), normalizeHex(theme.surface)) <
      MIN_TEXT_CONTRAST
  )
    warnings.push(
      "The text colour is hard to read on this background, so black or white is used instead.",
    );
  if (
    contrastRatio(normalizeHex(theme.accent), normalizeHex(theme.surface)) <
    MIN_LARGE_TEXT_CONTRAST
  )
    warnings.push(
      "The brand colour blends into the background; buttons stay legible, but accent text uses the text colour.",
    );
  return warnings;
}

/**
 * Inline CSS variables for a branded block. Put on the block's root element;
 * descendants read `var(--brand-…)`.
 */
export function brandThemeStyle(theme: ResolvedBrandTheme): CSSProperties {
  return {
    "--brand-surface": theme.surface,
    "--brand-raised": theme.raised,
    "--brand-border": theme.border,
    "--brand-ink": theme.ink,
    "--brand-ink-muted": theme.inkMuted,
    "--brand-accent": theme.accent,
    "--brand-accent-ink": theme.accentInk,
    "--brand-accent-text": theme.accentText,
    "--brand-highlight": theme.highlight,
    "--brand-highlight-ink": theme.highlightInk,
    backgroundColor: theme.surface,
    color: theme.ink,
  } as CSSProperties;
}

/** Light-theme glass tints (black alphas), re-declared for a light scope. */
const LIGHT_GLASS: Record<string, number> = {
  "glass-bg-01": 0.01,
  "glass-bg-02": 0.02,
  "glass-bg-025": 0.025,
  "glass-bg-03": 0.03,
  "glass-bg-04": 0.04,
  "glass-bg-05": 0.05,
  "glass-bg-06": 0.06,
  "glass-bg-07": 0.07,
  "glass-bg-08": 0.08,
  "glass-bg-09": 0.09,
  "glass-bg-10": 0.1,
  "glass-bg-12": 0.12,
  "glass-border-03": 0.03,
  "glass-border-05": 0.05,
  "glass-border-06": 0.06,
  "glass-border-08": 0.08,
  "glass-border-10": 0.1,
  "glass-border-11": 0.11,
  "glass-border-12": 0.12,
  "glass-border-14": 0.14,
  "glass-border-20": 0.2,
};

/** Whether a brand's surface is dark (light text) or light (dark text). */
export function isDarkBrand(theme: Pick<ResolvedBrandTheme, "surface">) {
  return readableInk(theme.surface) === "#FFFFFF";
}

/**
 * Props that turn an entire subtree into the brand: a page, a drawer, a
 * storefront. Where `brandThemeStyle` styles one block that was written to
 * read `--brand-*`, this remaps the app's own design tokens (`--color-…` in
 * globals.css) inside the subtree, so every existing component — headers,
 * cards, inputs, buttons — follows the brand without being touched.
 *
 * - A dark brand also sets `.dark`, so `dark:` utilities inside apply.
 * - A light brand sets `data-brand-scope="light"`, which the `dark` variant in
 *   globals.css excludes, so `dark:` utilities stand down even on a page
 *   that is otherwise in dark mode.
 *
 * Spread onto the root element: `<div {...brandScope(brand)}>`.
 */
export function brandScope(
  theme: ResolvedBrandTheme,
  options: { className?: string } = {},
): {
  className: string;
  style: CSSProperties;
  "data-brand-scope": "dark" | "light";
} {
  const dark = isDarkBrand(theme);
  const tertiary = mix(theme.surface, theme.ink, 0.6);
  const strong = mix(theme.surface, theme.ink, 0.26);
  const alt = mix(theme.surface, theme.ink, 0.12);
  const tokens: Record<string, string> = {
    background: theme.surface,
    "background-alt": theme.raised,
    surface: theme.raised,
    "surface-alt": alt,
    "surface-secondary": theme.raised,
    "surface-elevated": alt,
    "text-primary": theme.ink,
    "text-secondary": theme.inkMuted,
    "text-tertiary": tertiary,
    foreground: theme.ink,
    "muted-foreground": theme.inkMuted,
    border: theme.border,
    "border-strong": strong,
    divider: theme.border,
    input: strong,
    ring: theme.accent,
    "focus-ring": theme.accent,
    brand: theme.accent,
    "brand-hover": theme.accent,
    "brand-dark": theme.accentText,
    "brand-foreground": theme.accentInk,
    primary: theme.accent,
    "primary-foreground": theme.accentInk,
    accent: theme.accent,
    "accent-hover": theme.accent,
    "accent-foreground": theme.accentInk,
    secondary: theme.raised,
    "secondary-foreground": theme.ink,
    muted: theme.raised,
    card: theme.raised,
    "card-foreground": theme.ink,
    popover: alt,
    "popover-foreground": theme.ink,
  };
  const style: Record<string, string> = {};
  for (const [name, value] of Object.entries(tokens))
    style[`--color-${name}`] = value;
  if (!dark)
    for (const [name, alpha] of Object.entries(LIGHT_GLASS))
      style[`--color-${name}`] = `rgba(0, 0, 0, ${alpha})`;
  return {
    className: [dark ? "dark" : "", options.className ?? ""]
      .filter(Boolean)
      .join(" "),
    "data-brand-scope": dark ? "dark" : "light",
    style: {
      ...brandThemeStyle(theme),
      ...style,
      colorScheme: dark ? "dark" : "light",
    } as CSSProperties,
  };
}
